import { isLimitError } from "@/utils/limitErrors"
/**
 * FeedbackSubmissionModal - User feedback collection modal
 *
 * NOTE: This modal uses shadcn/ui Dialog component, following the new UI pattern.
 * Legacy modals (FeedbackModal, UpgradeModal) use ModalBase and will be migrated.
 * New modals should follow this shadcn/ui Dialog pattern.
 */
import { FileListUpload } from "@/components/common/FileListUpload/FileListUpload"
import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import {
  FeedbackPayload,
  useSubmitFeedback,
} from "@/services/api/feedbackService"
import { IconFeather } from "@tabler/icons-react"
import { useState } from "react"
import { StarRating } from "./StarRating"

interface FeedbackSubmissionModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type FeedbackType = FeedbackPayload["feedbackType"]

export function FeedbackSubmissionModal({
  open,
  onOpenChange,
}: FeedbackSubmissionModalProps) {
  const { translate } = useTranslation()
  const [feedbackType, setFeedbackType] = useState<FeedbackType | "">("")
  const [feedbackText, setFeedbackText] = useState("")
  const [rating, setRating] = useState<number | undefined>(undefined)
  const [attachments, setAttachments] = useState<File[]>([])

  const submitFeedback = useSubmitFeedback()

  const resetForm = () => {
    setFeedbackType("")
    setFeedbackText("")
    setRating(undefined)
    setAttachments([])
  }

  const handleClose = () => {
    resetForm()
    onOpenChange(false)
  }

  const handleSubmit = async () => {
    if (!feedbackType || !feedbackText.trim()) {
      return
    }

    try {
      await submitFeedback.mutateAsync({
        feedbackText: feedbackText.trim(),
        feedbackType: feedbackType,
        rating: feedbackType === "bug" ? undefined : rating,
        attachments: feedbackType === "bug" ? attachments : undefined,
      })
      toast.success(translate("FEEDBACK_SUBMISSION/SUCCESS"))
      trackEvent(AnalyticsEvent.FEEDBACK_SUBMITTED)
      handleClose()
    } catch (error) {
      if (!isLimitError(error)) {
        toast.error(translate("FEEDBACK_SUBMISSION/ERROR"))
      }
    }
  }

  const isSubmitDisabled =
    !feedbackType || !feedbackText.trim() || submitFeedback.isPending

  const handleFeedbackTypeChange = (value: string) => {
    const nextType = value as FeedbackType
    setFeedbackType(nextType)
    if (nextType === "bug") {
      setRating(undefined)
    } else {
      setAttachments([])
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <IconFeather className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center">
            {translate("FEEDBACK_SUBMISSION/TITLE")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {translate("FEEDBACK_SUBMISSION/DESCRIPTION")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Category Select */}
          <div className="grid gap-2">
            <Label htmlFor="category">
              {translate("FEEDBACK_SUBMISSION/CATEGORY_LABEL")}
            </Label>
            <Select
              value={feedbackType}
              onValueChange={handleFeedbackTypeChange}
            >
              <SelectTrigger id="category">
                <SelectValue
                  placeholder={translate(
                    "FEEDBACK_SUBMISSION/CATEGORY_PLACEHOLDER",
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">
                  {translate("FEEDBACK_SUBMISSION/CATEGORY_GENERAL")}
                </SelectItem>
                <SelectItem value="bug">
                  {translate("FEEDBACK_SUBMISSION/CATEGORY_BUG")}
                </SelectItem>
                <SelectItem value="feature_request">
                  {translate("FEEDBACK_SUBMISSION/CATEGORY_FEATURE")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {feedbackType === "bug" ? (
            <FileListUpload
              id="feedback-attachments"
              label={translate("FEEDBACK_SUBMISSION/ATTACHMENTS_LABEL")}
              helpText={translate("FEEDBACK_SUBMISSION/ATTACHMENTS_HELP")}
              accept="image/png,image/jpeg,image/webp"
              maxFiles={3}
              files={attachments}
              onFilesChange={setAttachments}
            />
          ) : (
            <div className="grid gap-2">
              <Label>{translate("FEEDBACK_SUBMISSION/RATING_LABEL")}</Label>
              <StarRating value={rating} onChange={setRating} />
            </div>
          )}

          {/* Feedback Text */}
          <div className="grid gap-2">
            <Label htmlFor="feedback-text">
              {translate("FEEDBACK_SUBMISSION/TEXT_LABEL")}
            </Label>
            <Textarea
              id="feedback-text"
              placeholder={translate("FEEDBACK_SUBMISSION/TEXT_PLACEHOLDER")}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              className="min-h-[120px] resize-none"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose}>
            {translate("FEEDBACK_SUBMISSION/CANCEL")}
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitDisabled}>
            {submitFeedback.isPending
              ? translate("FEEDBACK_SUBMISSION/SUBMITTING")
              : translate("FEEDBACK_SUBMISSION/SUBMIT")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
